import { getMembershipStatus } from '../utils/membershipStatus';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Upload, Users, LogOut, Search, Trash2, Shield, Edit2, X, Save, 
  ChevronDown, ChevronUp, RefreshCw, Download, UserPlus, Check, GripVertical, Star,
  Mail, FileDown, Clock, AlertTriangle, Send, ChevronRight, Eye, Key, Copy
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';

const API_URL = "";

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
  name: 180,
  email: 200,
  status: 90,
  phone: 120,
  address: 180,
  joined: 80,
  lastPayment: 110,
  access: 100,
  expires: 130,
  lastLogin: 110,
  passwordSet: 90,
  actions: 150
};

export const AdminMembers = () => {
  const { user, token, logout, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewFile, setImportPreviewFile] = useState(null);
  const [importConfirming, setImportConfirming] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [tempPasswordMode, setTempPasswordMode] = useState('generate'); // 'generate' | 'custom'
  const [tempPasswordCustom, setTempPasswordCustom] = useState('');
  const [tempPasswordResult, setTempPasswordResult] = useState(null);  // { email, temporary_password, mode }
  const [tempPasswordConfirmFor, setTempPasswordConfirmFor] = useState(null);  // member object pending confirmation
  const [tempPasswordSetting, setTempPasswordSetting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState('last_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const [resizing, setResizing] = useState(null);
  const tableRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // { id, name }
  const [adminTarget, setAdminTarget] = useState(null);    // { id, name }
  const [newMemberForm, setNewMemberForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    access: 'Member',
    active_member: true
  });
  const [artistOfMonth, setArtistOfMonth] = useState(null);
  const [settingArtist, setSettingArtist] = useState(null);

  // Member Tools state
  const [exportFilter, setExportFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailStep, setEmailStep] = useState('compose'); // compose | preview | confirm | sending | results
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [includeOptout, setIncludeOptout] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResults, setEmailResults] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignHistory, setShowCampaignHistory] = useState(false);

  // Renewal Reminder state
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalDays, setRenewalDays] = useState(30);
  const [renewalStep, setRenewalStep] = useState('compose'); // compose | preview | confirm | sending | results
  const [renewalSubject, setRenewalSubject] = useState('');
  const [renewalBody, setRenewalBody] = useState('');
  const [renewalIncludeOptout, setRenewalIncludeOptout] = useState(true);
  const [renewalPreview, setRenewalPreview] = useState(null);
  const [renewalSending, setRenewalSending] = useState(false);
  const [renewalResults, setRenewalResults] = useState(null);
  const navigate = useNavigate();

  // Column resize handlers
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    setResizing({ columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] });
  }, [columnWidths]);

  const handleMouseMove = useCallback((e) => {
    if (!resizing) return;
    const diff = e.clientX - resizing.startX;
    const newWidth = Math.max(60, resizing.startWidth + diff);
    setColumnWidths(prev => ({ ...prev, [resizing.columnKey]: newWidth }));
  }, [resizing]);

  const stopResize = useCallback(() => {
    setResizing(null);
  }, []);

  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', stopResize);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', stopResize);
      };
    }
  }, [resizing, handleMouseMove, stopResize]);

  useEffect(() => {
    if (token && isAdmin) {
      fetchMembers();
      fetchArtistOfMonth();
      fetchCampaigns();
    }
  }, [token, isAdmin]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const fetchArtistOfMonth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/members/artist-of-month`);
      if (response.ok) {
        const data = await response.json();
        setArtistOfMonth(data);
      }
    } catch (error) {
      console.error('Error fetching artist of month:', error);
    }
  };

  const handleSetArtistOfMonth = async (member) => {
    setSettingArtist(member.id);
    try {
      const params = new URLSearchParams({
        member_id: member.id
      });
      
      const response = await fetch(`${API_URL}/api/members/artist-of-month?${params}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success(`${member.first_name} ${member.last_name} set as Artist of the Month!`);
        fetchArtistOfMonth();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to set Artist of the Month');
      }
    } catch (error) {
      toast.error('Error setting Artist of the Month');
    } finally {
      setSettingArtist(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setImportPreview(null);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/admin/members/upload?dry_run=true`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        // Show preview modal — user must explicitly confirm
        setImportPreview(result);
        setImportPreviewFile(file);
      } else {
        toast.error(result.detail || 'Preview failed');
      }
    } catch (error) {
      toast.error('Error reading file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreviewFile) return;
    setImportConfirming(true);
    const formData = new FormData();
    formData.append('file', importPreviewFile);
    try {
      const response = await fetch(`${API_URL}/api/admin/members/upload?dry_run=false`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const result = await response.json();
      if (response.ok) {
        setImportResult(result);
        setImportPreview(null);
        setImportPreviewFile(null);
        toast.success(`Imported: ${result.written_creates} created, ${result.written_updates} updated`);
        fetchMembers();
      } else {
        toast.error(result.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Error during import');
    } finally {
      setImportConfirming(false);
    }
  };

  const handleSetTemporaryPassword = async (member) => {
    if (tempPasswordMode === 'custom') {
      const pw = tempPasswordCustom.trim();
      if (pw.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
    }
    setTempPasswordSetting(true);
    try {
      const body = tempPasswordMode === 'custom'
        ? JSON.stringify({ password: tempPasswordCustom.trim() })
        : '{}';
      const r = await fetch(`${API_URL}/api/admin/members/${member.id}/set-temporary-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body,
      });
      const data = await r.json();
      if (r.ok) {
        setTempPasswordResult({ email: data.email, temporary_password: data.temporary_password, mode: data.mode });
        setTempPasswordConfirmFor(null);
        setTempPasswordCustom('');
        setTempPasswordMode('generate');
        fetchMembers();  // refresh so the table reflects any changed flags
      } else {
        toast.error(data.detail || 'Failed to set temporary password');
      }
    } catch (e) {
      toast.error('Network error setting temporary password');
    } finally {
      setTempPasswordSetting(false);
    }
  };

  const handleCancelImport = () => {
    setImportPreview(null);
    setImportPreviewFile(null);
  };

  // Helpers: normalize membership display values (prefer canonical, fall back to legacy).
  // Dates may arrive as ISO ("2026-08-12T13:45:00+00:00") or plain "YYYY-MM-DD".
  // Return YYYY-MM-DD for date-input pre-fill; display can format elsewhere.
  const toDateInputValue = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
  };

  const getActivationRenewalDate = (member) =>
    member.membership_last_renewed_at
    || member.membership_last_payment_at
    || member.membership_renewed_at
    || member.last_payment_date
    || '';

  const getExpirationDate = (member) =>
    member.membership_expires_at
    || member.expiration_date
    || '';

  const getStartedDate = (member) =>
    member.membership_started_at
    || member.joined_date
    || member.created_at
    || '';

  const handleEditClick = (member) => {
    setEditingMember(member.id);
    // Map both canonical and legacy address field variants so the modal
    // always shows what the row shows.
    const addr =
      member.address || member.street_address || member.address_line1 || member.street || '';
    const city = member.city || '';
    const state = member.state || member.province || '';
    const zip =
      member.zip_code || member.zip || member.postal_code || member.postcode || '';
    setEditForm({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      address: addr,
      city: city,
      state: state,
      zip_code: zip,
      phone_home: member.phone_home || '',
      phone_mobile: member.phone_mobile || '',
      phone_work: member.phone_work || '',
      website: member.website || '',
      instagram: member.instagram || '',
      position: member.position || '',
      access: member.access || 'Member',
      active_member: ['active', 'lifetime'].includes(getMembershipStatus(member)),
      expected_membership_revision: member.membership_revision || null,
      lifetime_member: getMembershipStatus(member) === 'lifetime',
      expiration_date: member.expiration_date || '',
      // New canonical lifecycle fields (pre-filled via canonical first, then legacy fallback)
      membership_started_at: toDateInputValue(getStartedDate(member)),
      membership_last_renewed_at: toDateInputValue(getActivationRenewalDate(member)),
      membership_expires_at: toDateInputValue(getExpirationDate(member)),
      // Legacy / history fields visible in the Membership History section
      legacy_member_id: member.legacy_member_id || '',
      legacy_comments: member.legacy_comments || '',
      membership_type: member.membership_type || ''
    });
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      // Build payload. When admin edits canonical dates, also sync legacy display
      // fields so the table (which reads legacy first as a fallback) stays consistent.
      const payload = { ...editForm };
      if (editForm.membership_expires_at) {
        payload.expiration_date = editForm.membership_expires_at; // YYYY-MM-DD
      }
      if (editForm.membership_last_renewed_at) {
        payload.last_payment_date = editForm.membership_last_renewed_at; // YYYY-MM-DD
      }
      if (editForm.membership_started_at) {
        payload.joined_date = editForm.membership_started_at; // legacy display sync
      }

      const response = await fetch(`${API_URL}/api/admin/members/${editingMember}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Member updated successfully');
        setEditingMember(null);
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update member');
      }
    } catch (error) {
      toast.error('Error updating member');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    setDeleteTarget({ id: memberId, name: memberName });
  };

  const confirmDeleteMember = async () => {
    if (!deleteTarget) return;
    const memberId = deleteTarget.id;
    try {
      const response = await fetch(`${API_URL}/api/admin/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Member deleted');
        setMembers(members.filter(m => m.id !== memberId));
      } else {
        toast.error('Failed to delete member');
      }
    } catch (error) {
      toast.error('Error deleting member');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleSetAdmin = async (memberId, memberName) => {
    setAdminTarget({ id: memberId, name: memberName });
  };

  const confirmSetAdmin = async () => {
    if (!adminTarget) return;
    const memberId = adminTarget.id;
    try {
      const response = await fetch(`${API_URL}/api/admin/members/${memberId}/set-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Admin access granted');
        fetchMembers();
      } else {
        toast.error('Failed to grant admin access');
      }
    } catch (error) {
      toast.error('Error updating member');
    } finally {
      setAdminTarget(null);
    }
  };

  const handleToggleActive = async (member) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/members/${member.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active_member: !['active', 'lifetime'].includes(getMembershipStatus(member)), expected_membership_revision: member.membership_revision || null })
      });

      if (response.ok) {
        toast.success(`Member ${member.active_member ? 'deactivated' : 'activated'}`);
        fetchMembers();
      }
    } catch (error) {
      toast.error('Error updating member status');
    }
  };

  const handleAddMember = async () => {
    if (!newMemberForm.email || !newMemberForm.first_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/members?email=${encodeURIComponent(newMemberForm.email)}&first_name=${encodeURIComponent(newMemberForm.first_name)}&last_name=${encodeURIComponent(newMemberForm.last_name || '')}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Member added — password setup email sent');
        setShowAddModal(false);
        setNewMemberForm({
          email: '',
          first_name: '',
          last_name: '',
          access: 'Member',
          active_member: true
        });
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add member');
      }
    } catch (error) {
      toast.error('Error adding member');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // ── Member Tools handlers ──────────────────────────────────────────────────

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/members/export?filter=${exportFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.download = `cafa_members_${exportFilter}_${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleEmailPreview = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Please enter a subject and message body.');
      return;
    }
    setEmailSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/members/inactive-preview?include_newsletter_optout=${includeOptout}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Preview failed');
      const data = await response.json();
      setEmailPreview(data);
      setEmailStep('preview');
    } catch (err) {
      toast.error('Failed to load preview. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendCampaign = async () => {
    setEmailStep('sending');
    setEmailSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/members/email-campaign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: emailSubject,
          message_body: emailBody,
          include_newsletter_optout: includeOptout
        })
      });
      if (!response.ok) throw new Error('Send failed');
      const data = await response.json();
      setEmailResults(data);
      setEmailStep('results');
      fetchCampaigns();
    } catch (err) {
      toast.error('Campaign send failed. Please try again.');
      setEmailStep('confirm');
    } finally {
      setEmailSending(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-campaigns?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (err) {
      // silent fail
    }
  };

  const openEmailModal = () => {
    setEmailStep('compose');
    setEmailSubject('');
    setEmailBody('');
    setIncludeOptout(false);
    setEmailPreview(null);
    setEmailResults(null);
    setShowEmailModal(true);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEmailStep('compose');
  };

  // ── Renewal Reminder handlers ──────────────────────────────────────────────

  const DEFAULT_RENEWAL_BODY = `Dear {{first_name}},

We wanted to let you know that your Coppini Academy membership is scheduled to expire on {{expiration_date}}.

We hope you'll renew your membership and continue to be part of our wonderful artistic community!

To renew, please visit thecoppini.org or reply to this email and we'll be happy to assist.

With warm regards,
The Coppini Academy of Fine Art
membership@thecoppini.org`;

  const openRenewalModal = () => {
    setRenewalStep('compose');
    setRenewalSubject('Your Coppini Academy Membership is Expiring Soon');
    setRenewalBody(DEFAULT_RENEWAL_BODY);
    setRenewalIncludeOptout(true);
    setRenewalPreview(null);
    setRenewalResults(null);
    setRenewalDays(30);
    setShowRenewalModal(true);
  };

  const closeRenewalModal = () => {
    setShowRenewalModal(false);
    setRenewalStep('compose');
  };

  const handleRenewalPreview = async () => {
    if (!renewalSubject.trim() || !renewalBody.trim()) {
      toast.error('Please enter a subject and message body.');
      return;
    }
    setRenewalSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/members/renewal-preview?days=${renewalDays}&include_newsletter_optout=${renewalIncludeOptout}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Preview failed');
      const data = await response.json();
      setRenewalPreview(data);
      setRenewalStep('preview');
    } catch (err) {
      toast.error('Failed to load preview. Please try again.');
    } finally {
      setRenewalSending(false);
    }
  };

  const handleSendRenewalCampaign = async () => {
    setRenewalStep('sending');
    setRenewalSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/members/renewal-campaign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: renewalSubject,
          message_body: renewalBody,
          days: renewalDays,
          include_newsletter_optout: renewalIncludeOptout
        })
      });
      if (!response.ok) throw new Error('Send failed');
      const data = await response.json();
      setRenewalResults(data);
      setRenewalStep('results');
      fetchCampaigns();
    } catch (err) {
      toast.error('Renewal campaign send failed. Please try again.');
      setRenewalStep('confirm');
    } finally {
      setRenewalSending(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const exportToCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Status', 'Access', 'Phone', 'Address', 'City', 'State', 'Zip'];
    const rows = filteredMembers.map(m => [
      m.first_name,
      m.last_name,
      m.email,
      getMembershipStatus(m),
      m.access,
      m.mobile_phone || m.home_phone || '',
      m.address || '',
      m.city || '',
      m.state || '',
      m.zip_code || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Filter and sort members
  const filteredMembers = members
    .filter(member => {
      // Search across first/last/display name + email (case-insensitive, undefined-safe)
      const term = (searchTerm || '').trim().toLowerCase();
      const haystack = [
        member.first_name,
        member.last_name,
        member.display_name,
        member.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !term || haystack.includes(term);

      const status = getMembershipStatus(member);
      const isLifetime = status === 'lifetime';
      const isActiveOnly = status === 'active';
      const isExpired = status === 'expired';
      const isInactive = status === 'inactive' || isExpired;
      let matchesFilter = true;
      if (filterStatus === 'active') matchesFilter = isActiveOnly;
      else if (filterStatus === 'lifetime') matchesFilter = isLifetime;
      else if (filterStatus === 'inactive') matchesFilter = isInactive;
      else if (filterStatus === 'expired') matchesFilter = isExpired;
      else if (filterStatus === 'admin') matchesFilter = ['Administrator', 'Admin', 'Webmaster'].includes(member.access);

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';

      // Handle phone fields specially - check multiple phone fields
      if (sortField === 'phone_mobile') {
        aVal = a.phone_mobile || a.phone_home || a.phone_work || '';
        bVal = b.phone_mobile || b.phone_home || b.phone_work || '';
      }

      // Handle membership_status with normalized computed status to match header stats logic.
      // Order: Lifetime > Active > Inactive > Unknown / Legacy
      if (sortField === 'membership_status') {
        const rank = (m) => ({ lifetime: 1, active: 2, expired: 3, inactive: 4 }[getMembershipStatus(m)]);
        aVal = rank(a);
        bVal = rank(b);
      }

      // Canonical date fallback for Activation/Renewal column sort
      if (sortField === 'last_payment_date') {
        aVal = getActivationRenewalDate(a) || '';
        bVal = getActivationRenewalDate(b) || '';
      }
      if (sortField === 'expiration_date') {
        aVal = getExpirationDate(a) || '';
        bVal = getExpirationDate(b) || '';
      }
      if (sortField === 'joined_date') {
        aVal = getStartedDate(a) || '';
        bVal = getStartedDate(b) || '';
      }

      // Password Set status sort: Pending > Set > —
      if (sortField === 'password_set_status') {
        const rank = (m) => {
          if (m.password_setup_token) return 1; // pending bubbles up
          if (m.last_login_at) return 2; // active set
          return 3; // unknown
        };
        aVal = rank(a);
        bVal = rank(b);
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      // Handle empty values - push them to the end
      if (!aVal && bVal) return sortDirection === 'asc' ? 1 : -1;
      if (aVal && !bVal) return sortDirection === 'asc' ? -1 : 1;

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      // Show subtle indicator that column is sortable
      return <ChevronDown size={14} className="text-stone-300" />;
    }
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  if (authLoading) {
    return null; // AdminLayout handles loading state
  }

  return (
    <>
    <AdminLayout title="Member Management" subtitle="Manage member accounts and profiles">
      <div data-testid="admin-members-page">
      {/* Stats & Actions Bar */}
      <section className="py-4 border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              {(() => {
                const total = members.length;
              const lifetime = members.filter(m => getMembershipStatus(m) === 'lifetime').length;
              const active = members.filter(m => getMembershipStatus(m) === 'active').length;
              const inactive = members.filter(m => ['inactive', 'expired'].includes(getMembershipStatus(m))).length;
                const admins = members.filter(m => ['Administrator', 'Admin', 'Webmaster'].includes(m.access)).length;

                const cards = [
                  { key: 'total',    label: 'Total',    value: total,    color: 'text-stone-900', testid: 'members-header-total' },
                  { key: 'active',   label: 'Active',   value: active,   color: 'text-green-600',  testid: 'members-header-active' },
                  { key: 'lifetime', label: 'Lifetime', value: lifetime, color: 'text-amber-600',  testid: 'members-header-lifetime' },
                  { key: 'inactive', label: 'Inactive', value: inactive, color: 'text-stone-500',  testid: 'members-header-inactive' },
                  { key: 'admins',   label: 'Admins',   value: admins,   color: 'text-purple-600', testid: 'members-header-admins' },
                ];

                return cards.map(c => (
                  <div
                    key={c.key}
                    className="text-center"
                    role="group"
                    aria-label={`${c.label}: ${c.value}`}
                    data-testid={c.testid}
                  >
                    <div className={`text-2xl font-bold ${c.color}`} aria-hidden="true">{c.value}</div>
                    <div className="text-xs text-stone-500" aria-hidden="true">{c.label}</div>
                  </div>
                ));
              })()}
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchMembers} size="sm">
                <RefreshCw size={16} className="mr-1" /> Refresh
              </Button>
              <Button onClick={() => setShowAddModal(true)} size="sm" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <UserPlus size={16} className="mr-1" /> Add Member
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Member Tools Section */}
      <section className="py-4 border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Help banner — discoverability for password reset */}
          <div
            className="mb-3 flex items-start gap-2 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs leading-relaxed"
            data-testid="password-reset-help-banner"
          >
            <Key size={14} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>Member can't log in?</strong> Click the gold key icon in the Actions column to set a temporary password — works without email. You can either generate a random password or type a memorable one for the member to copy over the phone. They'll be required to change it on next login.
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {/* Export CSV — label + select/button grouped so the CSV button baseline matches the other two */}
            <div className="flex flex-col" data-testid="export-members-section">
              <label className="text-[10px] text-stone-500 mb-1 font-medium uppercase tracking-wide">
                Select Group for Export / Email
              </label>
              <div className="flex items-center gap-1.5">
                <select
                  value={exportFilter}
                  onChange={(e) => setExportFilter(e.target.value)}
                  className="px-2 py-1.5 border border-stone-300 rounded-md text-sm bg-white h-9"
                  data-testid="export-filter-select"
                  title="This controls which members are exported or emailed. It does not filter the table below."
                >
                  <option value="all">All Members</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                  <option value="lifetime">Lifetime Only</option>
                  <option value="newsletter">Newsletter Opt-In</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  disabled={exporting}
                  data-testid="export-members-csv-btn"
                  className="h-9"
                >
                  {exporting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-stone-600 mr-1" />
                  ) : (
                    <FileDown size={15} className="mr-1" />
                  )}
                  {exporting ? 'Exporting...' : 'Export Members CSV'}
                </Button>
              </div>
            </div>

            <div className="h-9 border-l border-stone-300 self-end" />

            {/* Email Inactive */}
            <Button
              variant="outline"
              size="sm"
              onClick={openEmailModal}
              data-testid="email-inactive-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50 h-9"
            >
              <Mail size={15} className="mr-1" /> Email Inactive Members
            </Button>

            {/* Renewal Reminders */}
            <Button
              variant="outline"
              size="sm"
              onClick={openRenewalModal}
              data-testid="renewal-reminders-btn"
              className="border-green-300 text-green-700 hover:bg-green-50 h-9"
            >
              <Clock size={15} className="mr-1" /> Send Renewal Reminders
            </Button>

            {/* Campaign History toggle */}
            {campaigns.length > 0 && (
              <button
                onClick={() => setShowCampaignHistory(!showCampaignHistory)}
                className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1 ml-1 self-end pb-2"
                data-testid="campaign-history-toggle"
              >
                <Clock size={13} /> {campaigns.length} recent campaign{campaigns.length !== 1 ? 's' : ''}
                <ChevronDown size={13} className={showCampaignHistory ? 'rotate-180' : ''} />
              </button>
            )}
          </div>
          <p className="text-[10px] text-stone-400 mt-1.5">This controls export/email tools and does not filter the table below.</p>

          {/* Campaign History Panel */}
          {showCampaignHistory && campaigns.length > 0 && (
            <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50 divide-y divide-stone-100" data-testid="campaign-history-panel">
              {campaigns.map((c, i) => (
                <div key={c.id || i} className="px-4 py-2 text-sm flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <span className="font-medium text-stone-800 truncate max-w-xs">{c.subject}</span>
                  <span className="text-green-700">{c.total_sent} sent</span>
                  {c.total_failed > 0 && <span className="text-red-600">{c.total_failed} failed</span>}
                  {c.total_skipped > 0 && <span className="text-stone-400">{c.total_skipped} skipped</span>}
                  <span className="text-stone-400 text-xs ml-auto">
                    {c.sent_by_name || c.sent_by_email} · {c.sent_at ? new Date(c.sent_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Upload Section */}
      <section className="py-4 border-b border-stone-200 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Upload size={18} className="text-stone-500" />
              <span className="text-sm font-medium text-stone-700">Import from Excel:</span>
            </div>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={uploading || importConfirming}
              className="max-w-xs text-sm"
              data-testid="file-upload"
            />
            <span className="text-xs text-stone-500">
              You'll see a preview before anything is written. Blank cells never overwrite existing values.
            </span>
            {uploading && (
              <div className="flex items-center gap-2 text-stone-600 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cafa-red"></div>
                Reading file...
              </div>
            )}
          </div>
          {importResult && (
            <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm" data-testid="import-result-summary">
              <div className="font-medium text-green-800">
                ✓ Import complete — {importResult.written_creates} new member{importResult.written_creates === 1 ? '' : 's'} created, {importResult.written_updates} member{importResult.written_updates === 1 ? '' : 's'} updated
              </div>
              <div className="text-xs text-green-700 mt-1">
                Skipped: {importResult.rows_skipped_no_email} no-email · {importResult.duplicate_emails_in_file?.length || 0} duplicate-email rows · {importResult.invalid_dates_total} invalid dates · {importResult.sentinel_dates_total} placeholder dates
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-4 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-md text-sm"
              data-testid="filter-status"
            >
              <option value="all">All Members</option>
              <option value="active">Active Only</option>
              <option value="lifetime">Lifetime Only</option>
              <option value="expired">Expired Only</option>
              <option value="inactive">Inactive Only</option>
              <option value="admin">Admins Only</option>
            </select>
            
            <div className="text-sm text-stone-500">
              Showing {filteredMembers.length} of {members.length}
            </div>
            
            <button
              onClick={() => setColumnWidths(DEFAULT_COLUMN_WIDTHS)}
              className="text-xs text-stone-400 hover:text-stone-600"
              title="Reset column widths"
            >
              Reset Columns
            </button>
          </div>
        </div>
      </section>

      {/* Members Table */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto" ref={tableRef}>
                <table className="w-full" style={{ minWidth: '1200px', tableLayout: 'fixed' }}>
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.name }}
                        onClick={() => handleSort('last_name')}
                      >
                        <div className="flex items-center gap-1">
                          Name <SortIcon field="last_name" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('name', e)}
                        />
                      </th>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.email }}
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center gap-1">
                          Email <SortIcon field="email" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('email', e)}
                        />
                      </th>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.status }}
                        onClick={() => handleSort('membership_status')}
                      >
                        <div className="flex items-center gap-1">
                          Status <SortIcon field="membership_status" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('status', e)}
                        />
                      </th>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.phone }}
                        onClick={() => handleSort('phone_mobile')}
                      >
                        <div className="flex items-center gap-1">
                          Phone <SortIcon field="phone_mobile" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('phone', e)}
                        />
                      </th>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.address }}
                        onClick={() => handleSort('address')}
                      >
                        <div className="flex items-center gap-1">
                          Address <SortIcon field="address" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('address', e)}
                        />
                      </th>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.joined }}
                        onClick={() => handleSort('joined_date')}
                      >
                        <div className="flex items-center gap-1">
                          Joined <SortIcon field="joined_date" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('joined', e)}
                        />
                      </th>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.lastPayment }}
                        onClick={() => handleSort('last_payment_date')}
                      >
                        <div className="flex items-center gap-1">
                          Activation / Renewal <SortIcon field="last_payment_date" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('lastPayment', e)}
                        />
                      </th>
                      <th 
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.access }}
                        onClick={() => handleSort('access')}
                      >
                        <div className="flex items-center gap-1">
                          Access <SortIcon field="access" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('access', e)}
                        />
                      </th>
                      <th
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 whitespace-nowrap"
                        style={{ width: columnWidths.lastLogin, minWidth: 100 }}
                        onClick={() => handleSort('last_login_at')}
                      >
                        <div className="flex items-center gap-1">
                          Last Login <SortIcon field="last_login_at" />
                        </div>
                      </th>
                      <th
                        className="text-left py-3 px-2 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 whitespace-nowrap"
                        style={{ width: columnWidths.passwordSet, minWidth: 80 }}
                        onClick={() => handleSort('password_set_status')}
                        title="Has the member set their password? Pending = setup link issued but not used yet"
                      >
                        <div className="flex items-center gap-1">
                          Password <SortIcon field="password_set_status" />
                        </div>
                      </th>
                      <th 
                        className="text-left py-3 px-2 pr-4 font-semibold text-stone-700 text-sm cursor-pointer hover:bg-stone-100 relative group whitespace-nowrap"
                        style={{ width: columnWidths.expires, minWidth: 120 }}
                        onClick={() => handleSort('expiration_date')}
                      >
                        <div className="flex items-center gap-1">
                          Expires <SortIcon field="expiration_date" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('expires', e)}
                        />
                      </th>
                      <th 
                        className="text-right py-3 px-2 font-semibold text-stone-700 text-sm whitespace-nowrap sticky right-0 z-10 bg-stone-50 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]"
                        style={{ width: columnWidths.actions, minWidth: 140 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {filteredMembers.map((member) => (
                      <React.Fragment key={member.id}>
                        <tr className={`group hover:bg-stone-50 ${editingMember === member.id ? 'bg-cafa-red-50' : ''}`}>
                          <td className="py-3 px-2" style={{ width: columnWidths.name }}>
                            <div className="flex items-center gap-2 overflow-hidden">
                              {member.photo_url ? (
                                <img 
                                  src={member.photo_url.startsWith('http') ? member.photo_url : `${API_URL}${member.photo_url}`}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 text-xs font-medium flex-shrink-0">
                                  {member.first_name?.[0]}{member.last_name?.[0]}
                                </div>
                              )}
                              <div className="overflow-hidden">
                                <div className="font-medium text-stone-900 truncate">
                                  {member.first_name} {member.last_name}
                                </div>
                                {member.position && (
                                  <div className="text-xs text-stone-500 truncate">{member.position}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-sm text-stone-600 truncate" style={{ width: columnWidths.email }}>{member.email}</td>
                          <td className="py-3 px-2" style={{ width: columnWidths.status }}>
                            {(() => {
                          const membershipStatus = getMembershipStatus(member);
                          const isLifetime = membershipStatus === 'lifetime';
                          const isActive = membershipStatus === 'active';
                          const isExpired = membershipStatus === 'expired';
                              let displayStatus = 'inactive';
                              if (isLifetime) {
                                displayStatus = 'lifetime';
                              } else if (isActive) {
                                displayStatus = 'active';
                              } else if (isExpired) {
                                displayStatus = 'expired';
                              }
                              
                              const statusConfig = {
                                active: { bg: 'bg-green-100 text-green-800', label: 'Active' },
                                lifetime: { bg: 'bg-amber-100 text-amber-800', label: 'Lifetime' },
                                expired: { bg: 'bg-red-100 text-red-800', label: 'Expired' },
                                inactive: { bg: 'bg-stone-100 text-stone-600', label: 'Inactive' }
                              };
                              const config = statusConfig[displayStatus];
                              return (
                                <span className={`px-2 py-1 text-xs font-medium rounded ${config.bg}`}>
                                  {config.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-2 text-sm text-stone-600 truncate" style={{ width: columnWidths.phone }}>
                            {member.phone_mobile || member.phone_home || member.phone_work || '-'}
                          </td>
                          <td className="py-3 px-2 text-sm text-stone-600 truncate" style={{ width: columnWidths.address }}>
                            {(() => {
                              const rowAddr = member.address || member.street_address || member.address_line1 || member.street || '';
                              const rowZip = member.zip_code || member.zip || member.postal_code || member.postcode || '';
                              return rowAddr ? (
                                <span title={`${rowAddr}${member.city ? `, ${member.city}` : ''}${member.state ? `, ${member.state}` : ''} ${rowZip}`}>
                                  {rowAddr}{rowZip && `, ${rowZip}`}
                                </span>
                              ) : '—';
                            })()}
                          </td>
                          <td className="py-3 px-2 text-sm text-stone-600" style={{ width: columnWidths.joined }}>
                            {(() => {
                              const d = getStartedDate(member);
                              if (!d) return '-';
                              const m = String(d).match(/^(\d{4}-\d{2}-\d{2})/);
                              return m ? m[1] : d;
                            })()}
                          </td>
                          <td className="py-3 px-2 text-sm text-stone-600" style={{ width: columnWidths.lastPayment }}>
                            {(() => {
                              const d = getActivationRenewalDate(member);
                              if (!d) return '-';
                              // Accept full ISO or YYYY-MM-DD; show YYYY-MM-DD
                              const m = String(d).match(/^(\d{4}-\d{2}-\d{2})/);
                              return m ? m[1] : d;
                            })()}
                          </td>
                          <td className="py-3 px-2" style={{ width: columnWidths.access }}>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              ['Administrator', 'Admin', 'Webmaster'].includes(member.access)
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-stone-100 text-stone-600'
                            }`}>
                              {member.access === 'Administrator' ? 'Admin' : member.access}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-xs text-stone-600 whitespace-nowrap" style={{ width: columnWidths.lastLogin, minWidth: 100 }}>
                            {(() => {
                              const d = member.last_login_at;
                              if (!d) return <span className="text-stone-400">Never</span>;
                              const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
                              return m ? `${m[1]}-${m[2]}-${m[3]}` : String(d).slice(0, 10);
                            })()}
                          </td>
                          <td className="py-3 px-2 text-xs whitespace-nowrap" style={{ width: columnWidths.passwordSet, minWidth: 80 }}>
                            {(() => {
                              const pendingSetup = !!member.password_setup_token;
                              if (pendingSetup) {
                                return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-medium">Pending</span>;
                              }
                              const everLoggedIn = !!member.last_login_at;
                              if (everLoggedIn) {
                                return <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200 font-medium">Set</span>;
                              }
                              return <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200 font-medium">—</span>;
                            })()}
                          </td>
                          <td className="py-3 px-2 pr-4 text-sm text-stone-600 whitespace-nowrap" style={{ width: columnWidths.expires, minWidth: 120 }}>
                            {(() => {
                              const isLifetime = member.membership_status === 'lifetime' || member.is_lifetime_member === true || member.lifetime_member === true;
                              if (isLifetime) return 'Lifetime';
                              const d = getExpirationDate(member);
                              if (!d) return '-';
                              const m = String(d).match(/^(\d{4}-\d{2}-\d{2})/);
                              return m ? m[1] : d;
                            })()}
                          </td>
                          <td
                            className={`py-3 px-2 sticky right-0 z-10 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)] ${editingMember === member.id ? 'bg-cafa-red-50' : 'bg-white group-hover:bg-stone-50'}`}
                            style={{ width: columnWidths.actions, minWidth: 140 }}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSetArtistOfMonth(member)}
                                disabled={settingArtist === member.id}
                                className={`${
                                  artistOfMonth?.artist?.id === member.id 
                                    ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50' 
                                    : 'text-stone-400 hover:text-amber-500 hover:bg-amber-50'
                                }`}
                                title={artistOfMonth?.artist?.id === member.id ? "Current Artist of the Month" : "Set as Artist of the Month"}
                                data-testid={`artist-month-${member.id}`}
                              >
                                {settingArtist === member.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent" />
                                ) : (
                                  <Star size={16} fill={artistOfMonth?.artist?.id === member.id ? "currentColor" : "none"} />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(member)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                title="Edit"
                                data-testid={`edit-${member.id}`}
                              >
                                <Edit2 size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setTempPasswordConfirmFor(member)}
                                className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                                title="Reset password — generate or set a temporary password without sending email"
                                aria-label="Reset password"
                                data-testid={`reset-password-${member.id}`}
                              >
                                <Key size={16} />
                              </Button>
                              {!['Administrator', 'Admin', 'Webmaster'].includes(member.access) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSetAdmin(member.id, `${member.first_name} ${member.last_name}`)}
                                  className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                                  title="Grant Admin Access — gives this member full admin panel access"
                                  aria-label="Grant Admin Access"
                                >
                                  <Shield size={16} />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMember(member.id, `${member.first_name} ${member.last_name}`)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                title="Delete"
                                data-testid={`delete-${member.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                {filteredMembers.length === 0 && (
                  <div className="text-center py-12 text-stone-500">
                    <Users size={48} className="mx-auto mb-4 text-stone-300" />
                    <p>No members found</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Edit Member Modal — fixed centered, viewport-independent */}
      {editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-member-modal">
          <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <h3 className="text-lg font-semibold text-stone-900">Edit Member</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingMember(null)}><X size={18} /></Button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">First Name</Label>
                  <Input value={editForm.first_name} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Last Name</Label>
                  <Input value={editForm.last_name} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="edit-member-email" className="text-xs">Email (used to sign in)</Label>
                  <Input id="edit-member-email" type="email" data-testid="edit-member-email-input" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="mt-1" />
                  <p className="text-[11px] text-stone-500 mt-1">The member signs in with the new address from now on and will need to sign in again.</p>
                </div>
                <div>
                  <Label className="text-xs">Position</Label>
                  <Input value={editForm.position} onChange={(e) => setEditForm({...editForm, position: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Access Level</Label>
                  <select value={editForm.access} onChange={(e) => setEditForm({...editForm, access: e.target.value})} className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md text-sm">
                    <option value="Member">Member</option>
                    <option value="Administrator">Administrator</option>
                    <option value="Webmaster">Webmaster</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Address</Label>
                  <Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">City</Label>
                  <Input value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={editForm.state} onChange={(e) => setEditForm({...editForm, state: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">ZIP Code</Label>
                  <Input value={editForm.zip_code} onChange={(e) => setEditForm({...editForm, zip_code: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Home Phone</Label>
                  <Input value={editForm.phone_home} onChange={(e) => setEditForm({...editForm, phone_home: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Mobile Phone</Label>
                  <Input value={editForm.phone_mobile} onChange={(e) => setEditForm({...editForm, phone_mobile: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Work Phone</Label>
                  <Input value={editForm.phone_work} onChange={(e) => setEditForm({...editForm, phone_work: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Website</Label>
                  <Input value={editForm.website} onChange={(e) => setEditForm({...editForm, website: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Instagram</Label>
                  <Input value={editForm.instagram} onChange={(e) => setEditForm({...editForm, instagram: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div className="flex flex-col gap-1 pt-1">
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editForm.active_member} onChange={(e) => setEditForm({...editForm, active_member: e.target.checked})} className="rounded border-stone-300" />
                    <span className="text-sm">Active Member</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editForm.lifetime_member} onChange={(e) => setEditForm({...editForm, lifetime_member: e.target.checked})} className="rounded border-stone-300" />
                    <span className="text-sm">Lifetime Member</span>
                  </label>
                </div>
                <p className="text-[11px] text-stone-500 mt-0.5">
                  Lifetime members are treated as active and do not expire.
                </p>
              </div>
              {/* Membership lifecycle dates (canonical) — legacy fields are kept in sync on save */}
              <div className="border-t border-stone-200 pt-3 mt-1">
                <div className="text-xs font-medium text-stone-700 mb-2">Membership Lifecycle Dates</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Membership Started / Joined</Label>
                    <Input
                      type="date"
                      value={editForm.membership_started_at || ''}
                      onChange={(e) => setEditForm({ ...editForm, membership_started_at: e.target.value })}
                      className="mt-1"
                      data-testid="edit-membership-started-at"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Activation / Renewal Date</Label>
                    <Input
                      type="date"
                      value={editForm.membership_last_renewed_at || ''}
                      onChange={(e) => setEditForm({ ...editForm, membership_last_renewed_at: e.target.value })}
                      className="mt-1"
                      data-testid="edit-membership-last-renewed-at"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Expiration Date</Label>
                    <Input
                      type="date"
                      value={editForm.membership_expires_at || ''}
                      onChange={(e) => setEditForm({ ...editForm, membership_expires_at: e.target.value })}
                      className="mt-1"
                      disabled={editForm.lifetime_member}
                      data-testid="edit-membership-expires-at"
                    />
                    {editForm.lifetime_member && (
                      <p className="text-[11px] text-stone-500 mt-1">Lifetime members do not expire.</p>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-stone-500 mt-2">
                  These are the canonical dates used going forward. Saving also syncs the legacy Expires / Last Paid display fields.
                </p>

                {/* Legacy / history fields — populated by import or admin edit */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div className="sm:col-span-1">
                    <Label className="text-xs">Legacy Member ID</Label>
                    <Input
                      value={editForm.legacy_member_id || ''}
                      onChange={(e) => setEditForm({ ...editForm, legacy_member_id: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., 12345"
                      data-testid="edit-legacy-member-id"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Membership Type</Label>
                    <Input
                      value={editForm.membership_type || ''}
                      onChange={(e) => setEditForm({ ...editForm, membership_type: e.target.value })}
                      className="mt-1"
                      placeholder="artist / patron / instructor / board / general / lifetime"
                      data-testid="edit-membership-type"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Legacy Comments / Notes</Label>
                  <textarea
                    value={editForm.legacy_comments || ''}
                    onChange={(e) => setEditForm({ ...editForm, legacy_comments: e.target.value })}
                    rows={3}
                    className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md text-sm"
                    placeholder="Imported notes or admin annotations about this member's history"
                    data-testid="edit-legacy-comments"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-stone-200 flex items-center gap-2 flex-wrap">
              <Button onClick={handleEditSave} disabled={saving} className="bg-cafa-red hover:bg-cafa-red-900 text-white" data-testid="save-member-edit-btn">
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <Save size={16} className="mr-2" />}
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setEditingMember(null)}>Cancel</Button>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const m = members.find(x => x.id === editingMember);
                    if (m) setTempPasswordConfirmFor(m);
                  }}
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="set-temp-password-btn"
                  title="Generate a one-time temporary password for this member. Use only when the member can't receive emails."
                >
                  <Key size={15} className="mr-2" />
                  Set Temporary Password
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add New Member</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newMemberForm.email}
                  onChange={(e) => setNewMemberForm({...newMemberForm, email: e.target.value})}
                  className="mt-1"
                  placeholder="member@example.com"
                />
              </div>
              <p className="text-xs text-stone-500">A password setup email will be sent automatically.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={newMemberForm.first_name}
                    onChange={(e) => setNewMemberForm({...newMemberForm, first_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={newMemberForm.last_name}
                    onChange={(e) => setNewMemberForm({...newMemberForm, last_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4 pt-4">
                <Button 
                  onClick={handleAddMember} 
                  disabled={saving}
                  className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white"
                >
                  {saving ? 'Adding...' : 'Add Member'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>

      {/* ── Set Temporary Password — Confirm Modal ─────────────────────── */}
      {tempPasswordConfirmFor && !tempPasswordResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="temp-password-confirm-modal">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-amber-100 p-2 text-amber-700">
                  <Key size={18} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-stone-900">Reset Password</h3>
                  <p className="text-sm text-stone-600 mt-1">
                    For <strong>{tempPasswordConfirmFor.first_name} {tempPasswordConfirmFor.last_name}</strong> ({tempPasswordConfirmFor.email})
                  </p>
                </div>
              </div>

              {/* Mode toggle */}
              <div className="grid grid-cols-2 gap-2" data-testid="temp-password-mode-toggle">
                <button
                  type="button"
                  onClick={() => setTempPasswordMode('generate')}
                  className={`px-3 py-2 rounded text-sm font-medium border transition-colors ${
                    tempPasswordMode === 'generate'
                      ? 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
                  }`}
                  data-testid="temp-password-mode-generate"
                >Generate random</button>
                <button
                  type="button"
                  onClick={() => setTempPasswordMode('custom')}
                  className={`px-3 py-2 rounded text-sm font-medium border transition-colors ${
                    tempPasswordMode === 'custom'
                      ? 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
                  }`}
                  data-testid="temp-password-mode-custom"
                >Choose my own</button>
              </div>

              {tempPasswordMode === 'custom' && (
                <div className="space-y-2" data-testid="temp-password-custom-section">
                  <Label className="text-sm">New password (min 8 chars)</Label>
                  <Input
                    value={tempPasswordCustom}
                    onChange={(e) => setTempPasswordCustom(e.target.value)}
                    placeholder="e.g. Coppini2026"
                    autoComplete="new-password"
                    spellCheck={false}
                    data-testid="temp-password-custom-input"
                  />
                  <p className="text-xs text-stone-500">
                    Pick something memorable the member can copy easily over the phone.
                  </p>
                </div>
              )}

              <ul className="text-xs text-stone-500 list-disc pl-4 space-y-0.5">
                <li>The new password will be shown <strong>once</strong>. Read it to the member by phone.</li>
                <li>The member must change it on next login.</li>
                <li>Any pending password-setup link is cleared.</li>
                <li>The member's email is auto-marked verified, so they won't hit the verify-email gate.</li>
              </ul>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setTempPasswordConfirmFor(null); setTempPasswordCustom(''); setTempPasswordMode('generate'); }} disabled={tempPasswordSetting}>Cancel</Button>
                <Button
                  onClick={() => handleSetTemporaryPassword(tempPasswordConfirmFor)}
                  disabled={tempPasswordSetting || (tempPasswordMode === 'custom' && tempPasswordCustom.trim().length < 8)}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="confirm-temp-password-btn"
                >
                  {tempPasswordSetting
                    ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Setting…</>
                    : <><Key size={15} className="mr-1.5" />{tempPasswordMode === 'custom' ? 'Set Password' : 'Generate Password'}</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Set Temporary Password — One-time Reveal Modal ──────────────── */}
      {tempPasswordResult && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4" data-testid="temp-password-reveal-modal">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="text-base font-semibold text-stone-900">Temporary Password Generated</h3>
                <p className="text-sm text-stone-600 mt-1">
                  This is the <strong>only</strong> time this password will be shown. Read it to <strong>{tempPasswordResult.email}</strong>, then close this dialog.
                </p>
              </div>
              <div className="rounded-md border border-stone-300 bg-stone-50 px-4 py-3 flex items-center justify-between gap-3">
                <code className="text-xl font-mono tracking-wider text-stone-900 select-all" data-testid="temp-password-value">{tempPasswordResult.temporary_password}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(tempPasswordResult.temporary_password);
                    toast.success('Copied');
                  }}
                  data-testid="copy-temp-password-btn"
                >
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                <strong>Important:</strong> The member is required to change this password on their next login. They will be redirected to a "Change Password" page automatically. If you close this dialog without saving the password, you'll need to generate another one.
              </div>
              <div className="flex items-center justify-end pt-1">
                <Button
                  onClick={() => setTempPasswordResult(null)}
                  className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                  data-testid="close-temp-password-btn"
                >
                  I've shared it with the member
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Import Preview Modal ───────────────────────────────────────────── */}
      {importPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="import-preview-modal">
          <Card className="w-full max-w-3xl my-8">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-stone-900">Import Preview — Dry Run</h2>
                  <p className="text-sm text-stone-500 mt-1">
                    Nothing has been written yet. Review the changes below, then click Confirm Import to apply.
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={handleCancelImport} disabled={importConfirming}>
                  <X size={16} />
                </Button>
              </div>

              {/* Top-line counts */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-stone-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-stone-900">{importPreview.total_rows_in_file}</div>
                  <div className="text-xs text-stone-500">Rows in file</div>
                </div>
                <div className="bg-emerald-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700" data-testid="preview-creates-count">{importPreview.creates_planned}</div>
                  <div className="text-xs text-emerald-700">Will create</div>
                </div>
                <div className="bg-blue-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700" data-testid="preview-updates-count">{importPreview.updates_planned}</div>
                  <div className="text-xs text-blue-700">Will update</div>
                </div>
                <div className="bg-amber-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{importPreview.rows_skipped_no_email + (importPreview.duplicate_emails_in_file?.length || 0)}</div>
                  <div className="text-xs text-amber-700">Will skip</div>
                </div>
              </div>

              {/* Field-level update counts */}
              {Object.keys(importPreview.field_update_counts || {}).length > 0 && (
                <div>
                  <div className="text-xs font-medium text-stone-700 mb-1.5">Field-level update counts</div>
                  <div className="rounded-md border border-stone-200 bg-stone-50 p-3 max-h-40 overflow-y-auto">
                    <ul className="text-xs text-stone-700 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                      {Object.entries(importPreview.field_update_counts).map(([field, count]) => (
                        <li key={field} className="flex justify-between"><span className="font-mono">{field}</span><span className="text-stone-500">{count} row{count === 1 ? '' : 's'}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Issues & conflicts */}
              {(importPreview.duplicate_emails_in_file?.length > 0
                || importPreview.invalid_dates_total > 0
                || importPreview.sentinel_dates_total > 0
                || importPreview.active_conflicts_total > 0
                || importPreview.unusual_membership_types_total > 0
                || importPreview.ignored_columns?.length > 0) && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-stone-700">Issues to review</div>
                  {importPreview.duplicate_emails_in_file?.length > 0 && (
                    <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="preview-duplicate-emails">
                      <div className="font-medium text-amber-800">⚠ {importPreview.duplicate_emails_in_file.length} duplicate email{importPreview.duplicate_emails_in_file.length === 1 ? '' : 's'} in file (will skip)</div>
                      <ul className="mt-1 text-amber-700 max-h-24 overflow-y-auto font-mono">
                        {importPreview.duplicate_emails_in_file.slice(0, 20).map((d, i) => (
                          <li key={i}>{d.email} (rows: {d.rows.join(', ')})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {importPreview.sentinel_dates_total > 0 && (
                    <div className="text-xs bg-stone-50 border border-stone-200 rounded p-2">
                      <div className="font-medium text-stone-800">{importPreview.sentinel_dates_total} placeholder date{importPreview.sentinel_dates_total === 1 ? '' : 's'} skipped (1900-01-01 etc.)</div>
                    </div>
                  )}
                  {importPreview.invalid_dates_total > 0 && (
                    <div className="text-xs bg-red-50 border border-red-200 rounded p-2">
                      <div className="font-medium text-red-800">{importPreview.invalid_dates_total} unparseable date{importPreview.invalid_dates_total === 1 ? '' : 's'}</div>
                      <ul className="mt-1 text-red-700 max-h-24 overflow-y-auto">
                        {(importPreview.invalid_dates || []).slice(0, 10).map((d, i) => (
                          <li key={i} className="font-mono">row {d.row} · {d.field}: {d.value}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {importPreview.active_conflicts_total > 0 && (
                    <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
                      <div className="font-medium text-blue-800">{importPreview.active_conflicts_total} active-status conflict{importPreview.active_conflicts_total === 1 ? '' : 's'} — using lifecycle dates as the source of truth</div>
                      <ul className="mt-1 text-blue-700 max-h-24 overflow-y-auto">
                        {(importPreview.active_conflicts || []).slice(0, 10).map((c, i) => (
                          <li key={i} className="font-mono">row {c.row} · {c.email}: {c.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {importPreview.unusual_membership_types_total > 0 && (
                    <div className="text-xs bg-stone-50 border border-stone-200 rounded p-2">
                      <div className="font-medium text-stone-800">{importPreview.unusual_membership_types_total} unusual Membership Type value{importPreview.unusual_membership_types_total === 1 ? '' : 's'} (kept as-is)</div>
                      <ul className="mt-1 text-stone-700 max-h-24 overflow-y-auto">
                        {(importPreview.unusual_membership_types || []).slice(0, 10).map((u, i) => (
                          <li key={i} className="font-mono">row {u.row}: {u.value}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {importPreview.ignored_columns?.length > 0 && (
                    <div className="text-xs bg-stone-50 border border-stone-200 rounded p-2">
                      <div className="font-medium text-stone-800">Columns in file that won't be imported (no match in schema):</div>
                      <div className="mt-1 text-stone-600 font-mono">{importPreview.ignored_columns.join(', ')}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Sample of changes */}
              {importPreview.sample_changes?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-stone-700 mb-1.5">Sample of changes (up to 8 rows)</div>
                  <div className="rounded-md border border-stone-200 bg-white max-h-60 overflow-y-auto divide-y divide-stone-100">
                    {importPreview.sample_changes.map((s, i) => (
                      <div key={i} className="p-2 text-xs">
                        <div className="font-medium text-stone-800">
                          row {s.row} · {s.email} · <span className={s.is_new ? 'text-emerald-700' : 'text-blue-700'}>{s.is_new ? 'NEW' : 'UPDATE'}</span>
                        </div>
                        <ul className="mt-1 ml-4 text-stone-600 font-mono">
                          {Object.entries(s.fields).slice(0, 8).map(([k, v]) => (
                            <li key={k} className="truncate"><span className="text-stone-500">{k}:</span> {String(v).slice(0, 80)}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-stone-200">
                <Button variant="outline" onClick={handleCancelImport} disabled={importConfirming} data-testid="import-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={importConfirming || (importPreview.creates_planned + importPreview.updates_planned === 0)}
                  className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                  data-testid="import-confirm-btn"
                >
                  {importConfirming ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Importing...</>
                  ) : (
                    <>Confirm Import — {importPreview.creates_planned} create, {importPreview.updates_planned} update</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Email Inactive Members Modal ──────────────────────────────────── */}      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="email-campaign-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Email Inactive Members</h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  {emailStep === 'compose' && 'Compose your message'}
                  {emailStep === 'preview' && 'Review recipients before sending'}
                  {emailStep === 'confirm' && 'Confirm to send'}
                  {emailStep === 'sending' && 'Sending emails...'}
                  {emailStep === 'results' && 'Campaign complete'}
                </p>
              </div>
              {emailStep !== 'sending' && (
                <Button variant="ghost" size="sm" onClick={closeEmailModal}><X size={18} /></Button>
              )}
            </div>

            {/* Step indicator */}
            {['compose', 'preview', 'confirm'].includes(emailStep) && (
              <div className="flex items-center gap-0 px-6 pt-3">
                {[['compose', '1', 'Compose'], ['preview', '2', 'Preview'], ['confirm', '3', 'Confirm']].map(([step, num, label], i) => (
                  <React.Fragment key={step}>
                    <div className={`flex items-center gap-1.5 text-xs font-medium ${emailStep === step ? 'text-stone-900' : 'text-stone-400'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${emailStep === step ? 'bg-amber-600 text-white' : ['compose', 'preview', 'confirm'].indexOf(emailStep) > i ? 'bg-green-500 text-white' : 'bg-stone-200 text-stone-500'}`}>{num}</span>
                      {label}
                    </div>
                    {i < 2 && <ChevronRight size={14} className="mx-1.5 text-stone-300" />}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-6 py-4">

              {/* STEP: COMPOSE */}
              {emailStep === 'compose' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Subject Line *</Label>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      className="mt-1"
                      placeholder="We miss you at Coppini Academy..."
                      data-testid="email-subject-input"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm font-medium">Message Body *</Label>
                      <div className="flex flex-wrap gap-1">
                        {['{{first_name}}','{{last_name}}','{{membership_status}}','{{expiration_date}}'].map(token => (
                          <button key={token} onClick={() => setEmailBody(b => b + token)}
                            className="text-xs px-1.5 py-0.5 bg-stone-100 hover:bg-stone-200 rounded font-mono text-stone-600 border border-stone-200">
                            {token}
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      rows={10}
                      className="w-full mt-1 px-3 py-2 border border-stone-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Dear {{first_name}},&#10;&#10;We noticed your membership has lapsed..."
                      data-testid="email-body-input"
                    />
                    <p className="text-xs text-stone-400 mt-1">Click tokens above to insert personalization. Plain text — line breaks are preserved.</p>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeOptout}
                        onChange={(e) => setIncludeOptout(e.target.checked)}
                        className="mt-0.5"
                        data-testid="include-optout-checkbox"
                      />
                      <div>
                        <span className="text-sm font-medium text-amber-800">Include members who have opted out of emails</span>
                        <p className="text-xs text-amber-700 mt-0.5">Only check this for direct membership status communications. Do not use for newsletters or marketing.</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* STEP: PREVIEW */}
              {emailStep === 'preview' && emailPreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-2xl font-bold text-green-700" data-testid="preview-will-receive">{emailPreview.will_receive_count}</div>
                      <div className="text-xs text-green-600 mt-0.5">Will Receive</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                      <div className="text-2xl font-bold text-stone-500" data-testid="preview-opted-out">{emailPreview.skipped_opted_out_count}</div>
                      <div className="text-xs text-stone-400 mt-0.5">Skipped (Opted Out)</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                      <div className="text-2xl font-bold text-stone-500" data-testid="preview-no-email">{emailPreview.skipped_no_email_count}</div>
                      <div className="text-xs text-stone-400 mt-0.5">Skipped (No Email)</div>
                    </div>
                  </div>

                  {emailPreview.will_receive_count === 0 && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-700 text-sm">
                      <AlertTriangle size={16} />
                      No eligible recipients found. Try enabling the opted-out override.
                    </div>
                  )}

                  {emailPreview.will_receive.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-stone-700 mb-2">Recipients ({emailPreview.will_receive_count})</p>
                      <div className="max-h-48 overflow-y-auto border border-stone-200 rounded-lg divide-y divide-stone-100" data-testid="preview-recipients-list">
                        {emailPreview.will_receive.map((r, i) => (
                          <div key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                            <span className="font-medium text-stone-800">{r.name}</span>
                            <span className="text-stone-400 text-xs">{r.email}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {emailPreview.skipped_opted_out.length > 0 && (
                    <div>
                      <p className="text-xs text-stone-500 mb-1">Skipped — opted out ({emailPreview.skipped_opted_out_count})</p>
                      <div className="max-h-24 overflow-y-auto border border-stone-100 rounded-lg bg-stone-50 divide-y divide-stone-100">
                        {emailPreview.skipped_opted_out.map((r, i) => (
                          <div key={i} className="px-3 py-1.5 flex items-center justify-between text-xs text-stone-400">
                            <span>{r.name}</span><span>{r.email}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP: CONFIRM */}
              {emailStep === 'confirm' && emailPreview && (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-300">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-amber-900">You are about to send this email to <span className="text-amber-700">{emailPreview.will_receive_count} inactive member{emailPreview.will_receive_count !== 1 ? 's' : ''}</span>.</p>
                        <p className="text-sm text-amber-700 mt-1">This action cannot be undone. Each recipient receives an individual email — their addresses are not visible to each other.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 text-sm">
                    <p className="font-medium text-stone-700">Subject:</p>
                    <p className="text-stone-600 mt-0.5">{emailSubject}</p>
                  </div>
                </div>
              )}

              {/* STEP: SENDING */}
              {emailStep === 'sending' && (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent" />
                  <p className="text-stone-600 font-medium">Sending emails, please wait...</p>
                  <p className="text-stone-400 text-sm">Do not close this window.</p>
                </div>
              )}

              {/* STEP: RESULTS */}
              {emailStep === 'results' && emailResults && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-3xl font-bold text-green-700" data-testid="results-sent">{emailResults.sent_count}</div>
                      <div className="text-xs text-green-600 mt-0.5">Emails Sent</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="text-3xl font-bold text-red-600" data-testid="results-failed">{emailResults.failed_count}</div>
                      <div className="text-xs text-red-500 mt-0.5">Failed</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                      <div className="text-3xl font-bold text-stone-500" data-testid="results-skipped">{emailResults.skipped_count}</div>
                      <div className="text-xs text-stone-400 mt-0.5">Skipped</div>
                    </div>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 text-xs text-stone-500">
                    <p>Sent by {user?.email} at {emailResults.sent_at ? new Date(emailResults.sent_at).toLocaleString() : 'now'}</p>
                    <p className="mt-0.5">Campaign ID: <span className="font-mono">{emailResults.campaign_id}</span></p>
                  </div>
                  {emailResults.failed_count > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm">
                      <AlertTriangle size={15} />
                      {emailResults.failed_count} email{emailResults.failed_count !== 1 ? 's' : ''} failed to send. Check backend logs for details.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-between">
              <div>
                {emailStep === 'preview' && (
                  <Button variant="outline" size="sm" onClick={() => setEmailStep('compose')}>
                    Back to Compose
                  </Button>
                )}
                {emailStep === 'confirm' && (
                  <Button variant="outline" size="sm" onClick={() => setEmailStep('preview')}>
                    Back to Preview
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {emailStep !== 'sending' && emailStep !== 'results' && (
                  <Button variant="outline" size="sm" onClick={closeEmailModal}>Cancel</Button>
                )}
                {emailStep === 'compose' && (
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleEmailPreview}
                    disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
                    data-testid="preview-recipients-btn"
                  >
                    {emailSending ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1.5" /> : <Eye size={15} className="mr-1.5" />}
                    Preview Recipients
                  </Button>
                )}
                {emailStep === 'preview' && (
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => setEmailStep('confirm')}
                    disabled={!emailPreview || emailPreview.will_receive_count === 0}
                    data-testid="proceed-to-confirm-btn"
                  >
                    <ChevronRight size={15} className="mr-1" /> Proceed
                  </Button>
                )}
                {emailStep === 'confirm' && (
                  <Button
                    size="sm"
                    className="bg-red-700 hover:bg-red-800 text-white"
                    onClick={handleSendCampaign}
                    data-testid="confirm-send-btn"
                  >
                    <Send size={15} className="mr-1.5" /> Send to {emailPreview?.will_receive_count} Members
                  </Button>
                )}
                {emailStep === 'results' && (
                  <Button size="sm" className="bg-stone-800 hover:bg-stone-900 text-white" onClick={closeEmailModal}>
                    Close
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Renewal Reminder Modal ──────────────────────────────────────── */}
      {showRenewalModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="renewal-campaign-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Send Renewal Reminders</h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  {renewalStep === 'compose' && 'Choose window and compose your message'}
                  {renewalStep === 'preview' && 'Review recipients expiring soon'}
                  {renewalStep === 'confirm' && 'Confirm before sending'}
                  {renewalStep === 'sending' && 'Sending emails...'}
                  {renewalStep === 'results' && 'Renewal reminders sent'}
                </p>
              </div>
              {renewalStep !== 'sending' && (
                <Button variant="ghost" size="sm" onClick={closeRenewalModal}><X size={18} /></Button>
              )}
            </div>

            {/* Step indicator */}
            {['compose', 'preview', 'confirm'].includes(renewalStep) && (
              <div className="flex items-center gap-0 px-6 pt-3">
                {[['compose', '1', 'Compose'], ['preview', '2', 'Preview'], ['confirm', '3', 'Confirm']].map(([step, num, label], i) => (
                  <React.Fragment key={step}>
                    <div className={`flex items-center gap-1.5 text-xs font-medium ${renewalStep === step ? 'text-stone-900' : 'text-stone-400'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${renewalStep === step ? 'bg-green-600 text-white' : ['compose','preview','confirm'].indexOf(renewalStep) > i ? 'bg-green-500 text-white' : 'bg-stone-200 text-stone-500'}`}>{num}</span>
                      {label}
                    </div>
                    {i < 2 && <ChevronRight size={14} className="mx-1.5 text-stone-300" />}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-6 py-4">

              {/* STEP: COMPOSE */}
              {renewalStep === 'compose' && (
                <div className="space-y-4">
                  {/* Days window selector */}
                  <div>
                    <Label className="text-sm font-medium">Send to members expiring within:</Label>
                    <div className="flex gap-2 mt-2">
                      {[30, 60, 90].map(d => (
                        <button
                          key={d}
                          onClick={() => setRenewalDays(d)}
                          data-testid={`renewal-days-${d}`}
                          className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            renewalDays === d
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-white text-stone-600 border-stone-300 hover:border-green-400'
                          }`}
                        >
                          {d} days
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Subject Line *</Label>
                    <Input
                      value={renewalSubject}
                      onChange={(e) => setRenewalSubject(e.target.value)}
                      className="mt-1"
                      data-testid="renewal-subject-input"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm font-medium">Message Body *</Label>
                      <div className="flex flex-wrap gap-1">
                        {['{{first_name}}','{{last_name}}','{{expiration_date}}','{{membership_type}}'].map(token => (
                          <button key={token} onClick={() => setRenewalBody(b => b + token)}
                            className="text-xs px-1.5 py-0.5 bg-stone-100 hover:bg-stone-200 rounded font-mono text-stone-600 border border-stone-200">
                            {token}
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={renewalBody}
                      onChange={(e) => setRenewalBody(e.target.value)}
                      rows={10}
                      className="w-full mt-1 px-3 py-2 border border-stone-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-green-500"
                      data-testid="renewal-body-input"
                    />
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={renewalIncludeOptout}
                        onChange={(e) => setRenewalIncludeOptout(e.target.checked)}
                        className="mt-0.5"
                        data-testid="renewal-include-optout-checkbox"
                      />
                      <div>
                        <span className="text-sm font-medium text-green-800">Include members who have opted out of marketing emails</span>
                        <p className="text-xs text-green-700 mt-0.5">Renewal reminders are membership communications — checked by default.</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* STEP: PREVIEW */}
              {renewalStep === 'preview' && renewalPreview && (
                <div className="space-y-4">
                  <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 text-sm text-stone-600">
                    Members expiring between <strong>{renewalPreview.window_start}</strong> and <strong>{renewalPreview.window_end}</strong>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-2xl font-bold text-green-700" data-testid="renewal-preview-will-receive">{renewalPreview.will_receive_count}</div>
                      <div className="text-xs text-green-600 mt-0.5">Will Receive</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                      <div className="text-2xl font-bold text-stone-500">{renewalPreview.skipped_opted_out_count}</div>
                      <div className="text-xs text-stone-400 mt-0.5">Opted Out</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                      <div className="text-2xl font-bold text-stone-500">{renewalPreview.skipped_no_email_count}</div>
                      <div className="text-xs text-stone-400 mt-0.5">No Email</div>
                    </div>
                  </div>
                  {renewalPreview.will_receive_count === 0 && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-700 text-sm">
                      <AlertTriangle size={16} /> No members found expiring in this window.
                    </div>
                  )}
                  {renewalPreview.will_receive.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-stone-700 mb-2">Recipients — sorted by expiration date</p>
                      <div className="max-h-52 overflow-y-auto border border-stone-200 rounded-lg divide-y divide-stone-100" data-testid="renewal-recipients-list">
                        {renewalPreview.will_receive.map((r, i) => (
                          <div key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                            <span className="font-medium text-stone-800">{r.name}</span>
                            <div className="flex items-center gap-3 text-xs text-stone-400">
                              <span>{r.email}</span>
                              <span className="font-medium text-amber-600">{r.expiration_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP: CONFIRM */}
              {renewalStep === 'confirm' && renewalPreview && (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-xl border border-green-300">
                    <div className="flex items-start gap-3">
                      <Clock size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-green-900">You are about to send renewal reminders to <span className="text-green-700">{renewalPreview.will_receive_count} member{renewalPreview.will_receive_count !== 1 ? 's' : ''}</span> expiring within {renewalDays} days.</p>
                        <p className="text-sm text-green-700 mt-1">Each recipient receives an individual email. Their addresses are not visible to each other.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 text-sm">
                    <p className="font-medium text-stone-700">Subject:</p>
                    <p className="text-stone-600 mt-0.5">{renewalSubject}</p>
                  </div>
                </div>
              )}

              {/* STEP: SENDING */}
              {renewalStep === 'sending' && (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-500 border-t-transparent" />
                  <p className="text-stone-600 font-medium">Sending renewal reminders...</p>
                  <p className="text-stone-400 text-sm">Do not close this window.</p>
                </div>
              )}

              {/* STEP: RESULTS */}
              {renewalStep === 'results' && renewalResults && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-3xl font-bold text-green-700" data-testid="renewal-results-sent">{renewalResults.sent_count}</div>
                      <div className="text-xs text-green-600 mt-0.5">Sent</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="text-3xl font-bold text-red-600" data-testid="renewal-results-failed">{renewalResults.failed_count}</div>
                      <div className="text-xs text-red-500 mt-0.5">Failed</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                      <div className="text-3xl font-bold text-stone-500" data-testid="renewal-results-skipped">{renewalResults.skipped_count}</div>
                      <div className="text-xs text-stone-400 mt-0.5">Skipped</div>
                    </div>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 text-xs text-stone-500">
                    <p>Sent by {user?.email} at {renewalResults.sent_at ? new Date(renewalResults.sent_at).toLocaleString() : 'now'}</p>
                    <p className="mt-0.5">Campaign ID: <span className="font-mono">{renewalResults.campaign_id}</span></p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-between">
              <div>
                {renewalStep === 'preview' && (
                  <Button variant="outline" size="sm" onClick={() => setRenewalStep('compose')}>Back to Compose</Button>
                )}
                {renewalStep === 'confirm' && (
                  <Button variant="outline" size="sm" onClick={() => setRenewalStep('preview')}>Back to Preview</Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {renewalStep !== 'sending' && renewalStep !== 'results' && (
                  <Button variant="outline" size="sm" onClick={closeRenewalModal}>Cancel</Button>
                )}
                {renewalStep === 'compose' && (
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleRenewalPreview}
                    disabled={renewalSending || !renewalSubject.trim() || !renewalBody.trim()}
                    data-testid="renewal-preview-btn"
                  >
                    {renewalSending ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1.5" /> : <Eye size={15} className="mr-1.5" />}
                    Preview Recipients
                  </Button>
                )}
                {renewalStep === 'preview' && (
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => setRenewalStep('confirm')}
                    disabled={!renewalPreview || renewalPreview.will_receive_count === 0}
                    data-testid="renewal-proceed-btn"
                  >
                    <ChevronRight size={15} className="mr-1" /> Proceed
                  </Button>
                )}
                {renewalStep === 'confirm' && (
                  <Button
                    size="sm"
                    className="bg-green-700 hover:bg-green-800 text-white"
                    onClick={handleSendRenewalCampaign}
                    data-testid="renewal-confirm-send-btn"
                  >
                    <Send size={15} className="mr-1.5" /> Send to {renewalPreview?.will_receive_count} Members
                  </Button>
                )}
                {renewalStep === 'results' && (
                  <Button size="sm" className="bg-stone-800 hover:bg-stone-900 text-white" onClick={closeRenewalModal}>
                    Close
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Member Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="text-red-600" size={20} />
              Permanently delete this member?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-700 space-y-2">
              <span className="block">
                You are about to permanently delete <strong>{deleteTarget?.name}</strong>.
              </span>
              <span className="block text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                This action <strong>cannot be undone</strong>. The member's record, payment history references, and login access will be removed. Consider marking the member as <em>inactive</em> instead.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-member-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDeleteMember}
              data-testid="delete-member-confirm"
            >
              <Trash2 size={14} className="mr-2" />
              Yes, Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Grant Admin Access Confirmation */}
      <AlertDialog open={!!adminTarget} onOpenChange={(open) => { if (!open) setAdminTarget(null); }}>
        <AlertDialogContent data-testid="grant-admin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
              <Shield className="text-amber-600" size={20} />
              Grant Admin Access?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-700 space-y-2">
              <span className="block">
                <strong>{adminTarget?.name}</strong> will be granted full administrator privileges.
              </span>
              <span className="block text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                Admins can view, edit, and delete <strong>any</strong> member record, manage workshops/exhibitions, and access payment data. Only grant this to trusted staff.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="grant-admin-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmSetAdmin}
              data-testid="grant-admin-confirm"
            >
              <Shield size={14} className="mr-2" />
              Grant Admin Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminMembers;
// Deployment trigger: 1768939653
